import React from 'react';

import { Input, Button, message, Typography } from 'antd';
import { Formik, ErrorMessage, yupToFormErrors } from 'formik';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { T } from 'react-translator-component';
import styled from 'styled-components';
import * as Yup from 'yup';

import { createChargePointGroup, updateChargePointGroup } from '../../hooks/chargepointGroups';


const StationGroupConfigFormWrapper = styled.div`
  form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    label:first-child {
      margin-right: 10px;
    }

    button {
      margin: 20px 0 20px 0;
    }
  }
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: bold;
`;

const FormInputWrapper = styled.div`
  width: 100%;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  @media (min-width: 1024px) {
    input:not([type='checkbox']) {
      min-width: 300px;
    }
  }

  input:hover,
  input:focus,
  input:active,
  input:disabled:hover,
  .ant-input-affix-wrapper:hover,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper:active,
  .ant-input-affix-wrapper:visited,
  .ant-input-affix-wrapper-focused,
  select:hover,
  select:focus,
  select:active,
  textarea:hover,
  textarea:focus,
  textarea:active {
    border-right-width: 0 !important;
    border-color: #40a9ff;
    outline: none;
    box-shadow: none;
  }

  input:disabled:hover {
    border-color: rgba(0, 0, 0, 0.3);
  }

  .ant-input-affix-wrapper {
    border-right-width: 0 !important;
  }

  input,
  .ant-input-affix-wrapper,
  select,
  textarea {
    font-size: 14px;
    font-weight: normal;
    border-radius: 0 0 1px 0;
    border-width: 0 0 1px 0;
    border-color: #000;
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const FormInputGroupWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  width: 100%;

  > div:first-child {
    margin-right: 40px;
  }
`;

const MessageBox = styled.div`
  color: red;
  font-size: 12px;
`;

const Section = styled.div`
  align-self: center;
  margin-top: 40px;
`;

const { Title } = Typography;

const StationGroupConfigForm = ({ initialValues, type, chargeBoxGroupPk = null }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const COUNTRY = () => {
    switch (localStorage.getItem('theme')) {
      case 'terpel':
      case 'test-terpel':
        return 'CO';
      case 'terpel_equador':
        return 'EC';
      default:
        return 'BR';
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors = {};
        if (!values.name) errors.name = 'O campo Nome é obrigatório.';
        if (!values.locationLatitude) errors.locationLatitude = 'O campo Latitude deve ser preenchido.';
        else if (values.locationLatitude < -90 || values.locationLatitude > 90) errors.locationLatitude = 'O campo Latitude deve ser entre -90 e 90.';
        if (!values.locationLongitude) errors.locationLongitude = 'O campo Longitude deve ser preenchido.';

        return errors;
      }}

      validationSchema={() => Yup.lazy((values) => {
        return Yup.object().shape({
          address: Yup.object().shape({
            street: Yup.string().nullable().required('Campo obrigatório.'),
            houseNumber: Yup.string().nullable().required('Campo obrigatório.'),
            zipCode: Yup.string().nullable().required('Campo obrigatório.'),
            city: Yup.string().nullable().required('Campo obrigatório.'),
            state: Yup.string().nullable().required('Campo obrigatório.'),
          })
        });
      })}

      onSubmit={(values, { setSubmitting }) => {

        if (values.address.country !== null) {
          values.address.empty = false;
        }

        setTimeout(async () => {
          let data = null;
          if (type === 'create') {
            data = await createChargePointGroup(values);
          } else if (type === 'update') {
            data = await updateChargePointGroup(values, chargeBoxGroupPk);
          }

          if (data !== null) {
            if (data.type === 'success') {
              if (type === 'create') {
                message.success('Configuração de Carregadores criado com sucesso!');
              } else {
                message.success('Configuração de Carregadores alterado com sucesso!');
              }
              setTimeout(() => {
                history.push('/station_groups');
              }, 1000);
            } else {
              message.error('Não foi possível realizar a operação. Certifique-se de que os campos foram corretamente preenchidos.');
            }
          } else {
            message.error('Não foi possível realizar a operação. Certifique-se de que os campos foram corretamente preenchidos.');
          }
          setSubmitting(false);
        }, 400);
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {
        return (
          <StationGroupConfigFormWrapper>
            <form onSubmit={handleSubmit}>
              <FormInputGroupWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Name')}:</FormLabel>
                  <Input
                    name='name'
                    label='name'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                  />
                  <ErrorMessage name='name' component={MessageBox} />
                </FormInputWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('External ID')}:</FormLabel>
                  <Input name='externalId' label='externalId' onChange={handleChange} onBlur={handleBlur} value={values.externalId} allowClear />
                  <ErrorMessage name='externalId' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>

              <Section>
                <Title level={5}>{T('Address/Location')}</Title>
              </Section>

              <FormInputGroupWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Street')}:</FormLabel>
                  <Input name='address.street' label='address.street' onChange={handleChange} onBlur={handleBlur} value={values.address.street} required maxLength={30} allowClear />
                  <ErrorMessage name='address.street' component={MessageBox} />
                </FormInputWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('House number')}:</FormLabel>
                  <Input name='address.houseNumber' label='address.houseNumber' onChange={handleChange} onBlur={handleBlur} value={values.address.houseNumber} required maxLength={5} allowClear />
                  <ErrorMessage name='address.houseNumber' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>

              <FormInputGroupWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Zip code')}:</FormLabel>
                  <Input name='address.zipCode' label='address.zipCode' onChange={handleChange} onBlur={handleBlur} value={values.address.zipCode} required maxLength={25} allowClear />
                  <ErrorMessage name='address.zipCode' component={MessageBox} />
                </FormInputWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('City')}:</FormLabel>
                  <Input name='address.city' label='address.city' onChange={handleChange} onBlur={handleBlur} value={values.address.city} required maxLength={25} allowClear />
                  <ErrorMessage name='address.city' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>

              <FormInputGroupWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('State')}:</FormLabel>
                  <Input name='address.state' label='address.state' onChange={handleChange} onBlur={handleBlur} value={values.address.state} required maxLength={25} allowClear />
                  <ErrorMessage name='address.state' component={MessageBox} />
                </FormInputWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Country')}:</FormLabel>
                  <Input name='address.country' label='address.country' onChange={handleChange} onBlur={handleBlur} value={values.address.country = COUNTRY()} required maxLength={25} readOnly disabled />
                  <ErrorMessage name='address.country' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>

              <FormInputGroupWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Location Latitude')}:</FormLabel>
                  <Input
                    type='number'
                    name='locationLatitude'
                    label='locationLatitude'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.locationLatitude}
                    maxLength={25}
                    allowClear
                    required
                  />
                  <ErrorMessage name='locationLatitude' component={MessageBox} />
                </FormInputWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Location Longitude')}:</FormLabel>
                  <Input
                    type='number'
                    name='locationLongitude'
                    label='locationLongitude'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.locationLongitude}
                    maxLength={25}
                    allowClear
                    required
                  />
                  <ErrorMessage name='locationLongitude' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>

              <Button type='primary' disabled={isSubmitting} onClick={handleSubmit}>
                {T('Submit')}
              </Button>
            </form>
          </StationGroupConfigFormWrapper>
        );
      }}
    </Formik>
  );
};

export default StationGroupConfigForm;
